import * as React from 'react';

import { TableControllerProps } from "./table.types";
import { NewTableColumnId, NewTableField, NewTableFolder } from '@common/components/new-table';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';

type Params = {
  withFirstActionsColumn: boolean;
};

export const useTableController = <Item extends { id: string | number; [key: string]: any }>(props: TableControllerProps<Item>, params: Params) => {
  const { itemUniqProperty, columns, selectedItems, getFields, getColumnId, onColumnsResize, onColumnsOrderChange, onSelectedItemsChange } = props;
  const { withFirstActionsColumn } = params;

  const [changingColumns, setChangingColumns] = React.useState(false);

  const handleSetCurrentSelectedRow = React.useCallback((id: Item['id'], item: Item) => {
    const isCurrentInSelectedAlready = selectedItems.some((x) => x[itemUniqProperty] === item[itemUniqProperty]);

    if (isCurrentInSelectedAlready) {
      return;
    }

    onSelectedItemsChange([item]);
  }, [selectedItems, onSelectedItemsChange]); 

  const handleColumnsResizing = React.useCallback(async (resizedColumns: Record<NewTableColumnId, number>) => {
    try {
      setChangingColumns(true);

      const processedColumns = columns.map((x) => {
        const newWidth = resizedColumns[getColumnId(x)];

        return {
          ...x,
          width: newWidth || x.width,
        };
      });
  
      await onColumnsResize(processedColumns);
    } finally {
      setChangingColumns(false);
    }
  }, [columns, onColumnsResize, getColumnId]);

  const handleColumnsOrderChange = React.useCallback(async (columnsId: Array<NewTableColumnId>) => {
    if (!onColumnsOrderChange) {
      return;
    }

    try {
      setChangingColumns(true);

      const columnsToSave: Array<ColumnListDto> = []
      
      columnsId.forEach((id) => {
        const column = columns.find((x) => getColumnId(x) === id);
  
        if (column) {
          columnsToSave.push(column);
        }
      });
  
      await onColumnsOrderChange(columnsToSave);
    } finally {
      setChangingColumns(false);
    }
  }, [columns, onColumnsOrderChange, getColumnId]);

  const handleColumnAdd = React.useCallback(async ({ field, columnId }: {
    field: NewTableField;
    folder: NewTableFolder;
    columnId: NewTableColumnId;
  }) => {
    if (!onColumnsOrderChange) {
      return;
    }

    try {
      setChangingColumns(true);

      let columnIndex = columns.findIndex((x) => getColumnId(x) === columnId);
  
      if (withFirstActionsColumn) {
        columnIndex = columnIndex + 1;
      }
  
      const column: ColumnListDto = {
        width: 100,
        field: {
          id: field.fieldId,
          name: field.fieldName,
          context: field.fieldContext,
          type: field.fieldTypeId,
          system: field.systemField
        },
        sortOrder: 0,
        sortAsc: undefined
  
      };
      const columnsCopy = [...columns];
      
      columnsCopy.splice(columnIndex, 0, column);
      
      await onColumnsOrderChange(columnsCopy);
    } finally {
      setChangingColumns(false);
    }
  }, [onColumnsOrderChange, columns, getColumnId, withFirstActionsColumn]);

  const handleColumnRemove = React.useCallback(async (columnId: NewTableColumnId) => {
    if (!onColumnsOrderChange) {
      return;
    }

    try {
      setChangingColumns(true);

      const columnIndex = columns.findIndex((x) => getColumnId(x) === columnId);
      const columnsCopy = [...columns];
  
      columnsCopy.splice(columnIndex, 1);
  
      await onColumnsOrderChange(columnsCopy);
    } finally {
      setChangingColumns(false);
    }
  }, [onColumnsOrderChange, columns, getColumnId]);

  const handleGetFields = React.useCallback((params: { searchKeyword: string; folderId: FolderPrimaryListDto['id']; system: boolean; }) => {
    if (!getFields) {
      return Promise.resolve([]);
    }

    const noFolderSelected = 0;

    return getFields({ 
      searchQuery: params.searchKeyword, 
      folderId: params.folderId || noFolderSelected, 
      system: params.system,
    });
  }, [getFields]);

  return {
    changingColumns,
    setChangingColumns,
    handleSetCurrentSelectedRow,
    handleColumnsResizing,
    handleColumnsOrderChange,
    handleGetFields,
    handleColumnRemove,
    handleColumnAdd,
  };
};